/* Analizar el pliego — los dos estados (pantalla 3b del rediseño).
 *
 * UN NOMBRE Y UN RESULTADO. Antes había «analizar proceso» en un botón y «análisis del pliego» en
 * el título, como si fueran cosas distintas. Aquí la acción es **Analizar el pliego** y el
 * resultado es **el informe del pliego**, en todas partes.
 *
 * Y es la única pieza que LEE los requisitos habilitantes, así que es la única que puede cambiar la
 * decisión. Por eso trae la caja «Cómo cambia tu veredicto» con el antes tachado y el después: el
 * «46 → 18» lo calcula `app/veredicto.py::ajustar()` en el servidor y llega en el evento
 * `veredicto`. Aquí no se resta nada.
 *
 * Estados, y no una pantalla que parpadea:
 *
 *   HOJA       qué documentos se van a leer, para poder cambiarlo antes de gastar.
 *   EN CURSO   el plan de lectura completo, documento a documento, con sus páginas y su estado.
 *   INFORME    titular = el hallazgo decisivo · la caja del veredicto · los hallazgos por
 *              severidad con su cita verbatim · qué te exigen · qué debes preparar.
 *
 * Y desde ago-2026 el informe SE PERSISTE (`app/corpus.py`, `_analisis.json`). Eso cambia la
 * pantalla de sitio: ya no es «la corrida», es «el informe de este proceso». Al entrar se pregunta
 * primero si ya existe; si existe se enseña y no cuesta nada. Antes, el informe vivía solo en el
 * estado de este componente, así que volver a la ficha lo destruía y entrar otra vez llamaba al
 * modelo y lo cobraba de nuevo sin que nadie lo hubiera pedido.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { ErrorApi, obtener, obtenerOpcional } from '../api/cliente'
import { flujoEventos } from '../api/sse'
import { entero, haceCuanto, normalizarNombre, plazo } from '../api/formato'
import { analizar, cruzarHallazgo, partirHallazgo, partirRespuesta } from '../api/markdown'
import type { Bloque, ClaseSecop, Cruce, Inline, Severidad } from '../api/markdown'
import type {
  AjusteVeredicto, AnalisisGuardado, DocumentoLeido, DocumentoPlan, EventoPliego, PlanPliego,
  RequisitoPliego, RespuestaFicha,
} from '../api/tipos'
import { ETIQUETA_BANDA } from '../api/formato'
import { usePerfil } from '../estado/perfil'
import { Banda } from '../ui/Banda'
import { IconoAviso, IconoNo, IconoSeguir, IconoSi, IconoSinDato, IconoVolver } from '../ui/Iconos'
import './Pliego.css'

export function Pliego() {
  const { id } = useParams()
  const navegar = useNavigate()
  /* El RECORRIDO de la bandeja («3 de 68», y el siguiente) viaja en el `state` del router. Esta
     pantalla lo recibía y no lo devolvía, así que ir a la ficha → pliego → ficha lo perdía: la
     segunda ficha aparecía sin contador y sin «siguiente», y para recuperarlo había que volver a
     la bandeja. Se reenvía TAL CUAL en las tres salidas. */
  const { state: recorrido } = useLocation()
  const { marcar } = usePerfil()

  const [ficha, setFicha] = useState<RespuestaFicha | null>(null)
  const [docs, setDocs] = useState<DocumentoLeido[]>([])
  const [paso, setPaso] = useState('Preparando la lectura…')
  const [ajuste, setAjuste] = useState<AjusteVeredicto | null>(null)
  const [informe, setInforme] = useState<string | null>(null)
  const [analizados, setAnalizados] = useState<DocumentoLeido[]>([])
  const [error, setError] = useState<string | null>(null)
  const [corriendo, setCorriendo] = useState(false)
  /* La HOJA DE CONFIRMACIÓN del 7b. Antes esta pantalla arrancaba el análisis al montarse: pulsar
     «Analizar el pliego» era un clic a ciegas que gastaba un minuto y unos centavos sobre una
     selección que nadie había visto. Ahora primero se enseña qué se va a leer y se puede cambiar. */
  const [plan, setPlan] = useState<PlanPliego | null>(null)
  const [elegidos, setElegidos] = useState<Set<string>>(new Set())
  const [arranca, setArranca] = useState(false)
  const [descartado, setDescartado] = useState(false)
  /* El análisis YA hecho de este proceso, si lo hay. Es la razón de ser de esta pantalla ahora:
     antes el informe vivía solo aquí, así que volver a la ficha lo destruía y entrar otra vez
     volvía a llamar al modelo y a cobrarlo. Ahora se lee del servidor y no cuesta nada.
     `undefined` = todavía no se sabe; `null` = nadie ha analizado este pliego. */
  const [guardado, setGuardado] = useState<AnalisisGuardado | null | undefined>(undefined)
  /* «Volver a analizar»: lo único que vuelve a pagar, y solo si alguien lo pide a propósito. */
  const [rehacer, setRehacer] = useState(false)
  const [analizadoEn, setAnalizadoEn] = useState<number | null>(null)
  const [viejo, setViejo] = useState(false)
  const corte = useRef<AbortController | null>(null)
  /* UNA sola lectura por proceso, aunque el efecto se monte dos veces.
   *
   * React llama los efectos DOS veces en StrictMode (desarrollo) para destapar limpiezas mal
   * hechas. Aquí eso no es un aviso teórico: cada montaje descarga los pliegos y llama al modelo,
   * así que la comprobación se pagaría el doble en dinero y en minuto y medio de espera. La
   * referencia sobrevive al remontaje; el `AbortController` no. */
  const arrancado = useRef<string | null>(null)

  useEffect(() => {
    if (!id) return
    const c = new AbortController()
    obtener<RespuestaFicha>(`/api/oportunidades/${id}`, c.signal)
      .then(setFicha)
      .catch(() => { if (!c.signal.aborted) setFicha(null) })
    return () => c.abort()
  }, [id])

  /* ¿Ya está analizado? Esta es la PRIMERA pregunta, antes que el plan de lectura: si la respuesta
     es que sí, no hay nada que planear ni que confirmar — solo que enseñar. */
  useEffect(() => {
    if (!id) return
    const c = new AbortController()
    obtenerOpcional<AnalisisGuardado>(
      `/api/analizar-proceso/guardado?id=${encodeURIComponent(id)}`, c.signal)
      .then((g) => {
        setGuardado(g)
        if (!g) return
        setInforme(g.informe)
        setAjuste(g.veredicto)
        setAnalizados(g.documentos_analizados ?? [])
        setAnalizadoEn(g.analizado_en || null)
        setViejo(!!g.desactualizado)
      })
      // Que no se pueda leer lo guardado NO es un error de cara al usuario: se cae al camino
      // normal, que es analizar. Enseñar un fallo aquí sería alarmar por una optimización.
      .catch(() => { if (!c.signal.aborted) setGuardado(null) })
    return () => c.abort()
  }, [id])

  // El plan, que NO lee nada ni gasta nada: solo dice qué se leería. Se pide solo si hace falta:
  // con un análisis ya hecho no hay nada que confirmar.
  useEffect(() => {
    if (!id || guardado === undefined || (guardado && !rehacer)) return
    const c = new AbortController()
    obtener<PlanPliego>(`/api/analizar-proceso/plan?id=${encodeURIComponent(id)}`, c.signal)
      .then((p) => {
        setPlan(p)
        setElegidos(new Set(p.documentos.filter((d) => d.sugerido).map((d) => d.nombre)))
      })
      .catch((e: unknown) => {
        if (!c.signal.aborted) setError(e instanceof ErrorApi ? e.message : String(e))
      })
    return () => c.abort()
  }, [id, guardado, rehacer])

  useEffect(() => {
    if (!id || !arranca || arrancado.current === id) return
    arrancado.current = id
    const c = new AbortController()
    corte.current = c
    setCorriendo(true)

    void (async () => {
      try {
        const cuerpo = { id, documentos: [...elegidos], forzar: rehacer }
        for await (const ev of flujoEventos<EventoPliego>('/api/analizar-proceso', cuerpo, c.signal)) {
          switch (ev.type) {
            case 'paso':
              setPaso(ev.texto)
              break
            case 'documentos':
              setDocs(ev.items.map((d) => ({ ...d, estado: 'cola' as const })))
              setPaso(ev.del_corpus
                ? 'Ya teníamos el texto de este proceso: no hay que descargar nada.'
                : 'Descargando y leyendo los documentos…')
              break
            case 'documento':
              setDocs((ds) => ds.map((d, i) => (i === ev.i
                ? { ...d, estado: ev.estado, ...(ev.paginas ? { paginas: ev.paginas } : {}) }
                : d)))
              break
            case 'veredicto': {
              const { type: _t, ...resto } = ev
              setAjuste(resto)
              break
            }
            case 'informe':
              setInforme(ev.markdown)
              setAnalizados(ev.documentos_analizados ?? [])
              setAnalizadoEn(ev.analizado_en || Math.floor(Date.now() / 1000))
              setViejo(!!ev.desactualizado)
              break
            case 'error':
              setError(ev.texto)
              break
            default:
              break
          }
        }
      } catch (e) {
        if (!c.signal.aborted) setError(e instanceof ErrorApi ? e.message : String(e))
      } finally {
        // Guardado igual que el `catch`: un flujo abortado no puede apagar el indicador del flujo
        // que sigue vivo. Sin esto, «Cancelar» desaparecía a los ocho segundos y la pantalla
        // parecía terminada mientras el modelo seguía redactando.
        if (!c.signal.aborted) setCorriendo(false)
      }
    })()

    // Sin `abort()` en la limpieza: el remontaje de StrictMode cortaría la única lectura que hay.
    // Lo que sí corta es «Cancelar», que llama a `corte.current.abort()` a propósito.
    //
    // `elegidos` NO va en las dependencias a propósito: se lee al arrancar y a partir de ahí la
    // corrida es suya. Ponerlo relanzaría el análisis —y el gasto— con cada casilla que alguien
    // tocara mientras corre.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, arranca])

  const proceso = ficha?.procesos?.[0]
  const entidad = proceso ? ficha?.entidades?.[proceso.entidad_nit] : undefined
  const bloques = useMemo(() => (informe ? analizar(informe) : []), [informe])

  /** Marcar o desmarcar un documento del plan, respetando el tope. */
  const alternarDoc = useCallback((d: DocumentoPlan) => {
    if (d.estado === 'sin_texto') return
    setElegidos((prev) => {
      const s = new Set(prev)
      if (s.has(d.nombre)) s.delete(d.nombre)
      else if (s.size < (plan?.tope ?? 5)) s.add(d.nombre)
      return s
    })
  }, [plan])

  /* «Volver a analizar» NO relanza en el acto: devuelve a la HOJA de confirmación, que es donde ya
     se dice qué se va a leer y cuánto tarda. Reusarla en vez de inventar un diálogo de «¿seguro?»
     mantiene una sola puerta al gasto, y de paso deja cambiar los documentos — que suele ser el
     motivo real de rehacerlo. */
  const volverAAnalizar = useCallback(() => {
    setRehacer(true)
    setInforme(null)
    setAjuste(null)
    setError(null)
    setArranca(false)
    arrancado.current = null
  }, [])

  const descartar = useCallback(() => {
    if (!id) return
    marcar(id, 'desc', 'No cumplo requisitos',
           ajuste?.decisivo ? `Del pliego: ${ajuste.decisivo}. ${ajuste.prosa}` : undefined)
    setDescartado(true)
  }, [id, marcar, ajuste])

  if (!id) return null

  return (
    <div className="pliego">
      <div className="pliego-barra">
        <Link className="accion" to={`/proceso/${id}`} state={recorrido}>
          <IconoVolver /> Ficha{proceso?.referencia ? ` · ${proceso.referencia}` : ''}
        </Link>
        {entidad?.nombre && (
          <span className="pliego-quien" title={entidad.nombre}>
            {normalizarNombre(entidad.nombre)}
            {proceso?.fecha_cierre ? ` · ${plazo(proceso.fecha_cierre).toLowerCase()}` : ''}
          </span>
        )}
      </div>

      {guardado === undefined ? (
        // Ni «Analizar» ni «Ver el análisis» hasta saber cuál de los dos es. Enseñar la hoja de
        // confirmación aquí la haría parpadear a informe medio segundo después.
        <div className="pliego-cargando" role="status">Buscando el análisis de este pliego…</div>
      ) : error && !informe ? (
        <div className="pliego-fallo">
          <h1>No pude leer el pliego</h1>
          <p>{error}</p>
          <p>
            <Link to={`/proceso/${id}`}>Volver a la ficha</Link> — los documentos siguen ahí para
            revisarlos a mano.
          </p>
        </div>
      ) : informe ? (
        <Informe bloques={bloques} ajuste={ajuste} analizados={analizados}
                 descartado={descartado} onDescartar={descartar}
                 analizadoEn={analizadoEn} desactualizado={viejo}
                 onRehacer={volverAAnalizar}
                 onPreguntar={() => navegar(`/asistente?proceso=${encodeURIComponent(id)}`)} />
      ) : arranca ? (
        <EnCurso docs={docs} paso={paso} ajuste={ajuste} corriendo={corriendo}
                 onCancelar={() => { corte.current?.abort(); navegar(`/proceso/${id}`, { state: recorrido }) }} />
      ) : (
        <Hoja plan={plan} elegidos={elegidos} onAlternar={alternarDoc}
              onArrancar={() => setArranca(true)}
              onVolver={() => navegar(`/proceso/${id}`, { state: recorrido })} />
      )}
    </div>
  )
}

/* ── estado 1 · en curso ──────────────────────────────────────────────────── */

function EnCurso({ docs, paso, ajuste, corriendo, onCancelar }: {
  docs: DocumentoLeido[]
  paso: string
  ajuste: AjusteVeredicto | null
  corriendo: boolean
  onCancelar: () => void
}) {
  const leidos = docs.filter((d) => d.estado === 'leido' || d.estado === 'ilegible').length
  // El avance mide DOCUMENTOS leídos, y el redactado del informe pesa como un documento más: sin
  // eso la barra se queda clavada en 100 % durante el minuto largo que tarda el modelo.
  const total = docs.length + 1
  const pct = docs.length ? Math.round((leidos / total) * 100) : 6

  return (
    <div className="curso">
      <div className="curso-cabeza">
        <h1>Leyendo el pliego</h1>
        {/* El diseño dice aquí «puedes seguir navegando: te avisamos cuando esté y queda guardado en
            la ficha». Hoy no es cierto ninguna de las dos cosas: no hay aviso —el repositorio no
            tiene infraestructura de correo— y el informe no se persiste. Lo que SÍ es cierto es que
            el texto extraído queda en el corpus, así que la segunda vez es casi inmediata. Se dice
            eso, que es lo que de verdad se puede prometer. */}
        <p>
          Tarda un minuto o dos y conviene no cerrar esta pestaña: si te vas, la lectura se detiene.
          El texto que ya se extrajo queda guardado, así que volver a entrar es mucho más rápido.
        </p>
      </div>

      <div className="curso-barra" aria-hidden="true"><i style={{ width: `${pct}%` }} /></div>
      <p className="curso-paso" role="status">{paso}</p>

      {docs.length > 0 && (
        <ul className="curso-docs">
          {docs.map((d, i) => (
            <li key={i} className={`doc-${d.estado ?? 'cola'}`}>
              <span className="doc-marca" aria-hidden="true">
                {d.estado === 'leido' ? <IconoSi />
                  : d.estado === 'ilegible' ? <IconoAviso />
                  : d.estado === 'leyendo' ? <span className="doc-punto" />
                  : <IconoSinDato />}
              </span>
              <span className="doc-nombre">
                {limpiarNombre(d.nombre) || d.categoria}
                {d.paginas ? ` · ${entero(d.paginas)} ${d.paginas === 1 ? 'página' : 'páginas'}` : ''}
              </span>
              <span className="doc-estado">
                {d.estado === 'leido' ? 'leído'
                  : d.estado === 'leyendo' ? 'leyendo…'
                  : d.estado === 'ilegible' ? 'no se pudo leer'
                  : 'en cola'}
              </span>
            </li>
          ))}
        </ul>
      )}

      {/* Los habilitantes salen antes que el informe, así que el veredicto ya se puede enseñar
          mientras el modelo redacta. Es lo que decide, y hacerlo esperar por la prosa sería hacerlo
          esperar por otra cosa. */}
      {ajuste && <CajaVeredicto ajuste={ajuste} compacta />}

      <div className="curso-que">
        <span className="rotulo">Qué va a buscar</span>
        <p>
          Qué se contrata · qué te exigen (experiencia, índices financieros, requisitos técnicos) ·
          qué debes preparar · y si hay requisitos que parecen hechos a la medida de otro proveedor.
        </p>
      </div>

      <div className="curso-acciones">
        {corriendo && (
          <button type="button" className="accion" onClick={onCancelar}>
            Cancelar y volver a la ficha
          </button>
        )}
      </div>
    </div>
  )
}

/**
 * La hoja de confirmación (7b): «voy a leer estos N documentos, cambia lo que quieras».
 *
 * Es el cambio de fondo de esta pantalla. Antes, pulsar «Analizar el pliego» arrancaba la lectura
 * al montarse el componente: un clic a ciegas que gastaba un minuto y unos centavos sobre una
 * selección que nadie había visto. El tope de documentos por corrida existía y era invisible.
 *
 * Los tres estados de un documento se enseñan distintos porque son cosas distintas. Y los que no
 * se pueden leer APARECEN, deshabilitados y con su motivo: «no aparece» y «aparece y no se puede»
 * son mensajes muy diferentes para quien está buscando su pliego.
 */
function Hoja({ plan, elegidos, onAlternar, onArrancar, onVolver }: {
  plan: PlanPliego | null
  elegidos: Set<string>
  onAlternar: (d: DocumentoPlan) => void
  onArrancar: () => void
  onVolver: () => void
}) {
  if (!plan) return <p className="rotulo">Viendo qué documentos hay…</p>

  const elegibles = plan.documentos.filter((d) => d.estado !== 'sin_texto')
  const conOcr = plan.documentos.some((d) => elegidos.has(d.nombre) && d.metodo === 'ocr')

  if (!elegibles.length) {
    return (
      <div className="pliego-fallo">
        <h1>No hay documentos que pueda leer</h1>
        <p>
          Este proceso no publicó documentos de licitación en un formato del que sepamos sacar
          texto. Puede que sean anteriores a 2025 —fuera de la cobertura del dato— o que tengan
          nombres opacos y estén clasificados en otro grupo.
        </p>
        <p><button type="button" className="accion" onClick={onVolver}>Volver a la ficha</button></p>
      </div>
    )
  }

  return (
    <div className="hoja">
      <div className="hoja-cabeza">
        <h1>Voy a leer estos {entero(elegidos.size)} documentos</h1>
        <p>
          Los elegí por orden de lectura. Cambia lo que quieras: son <b>{entero(plan.tope)} por
          corrida</b> y cada corrida tarda cerca de un minuto y gasta tokens.
        </p>
      </div>

      <p className="hoja-cupo" role="status">
        Llevas <b>{entero(elegidos.size)} de {entero(plan.tope)}</b> elegidos
      </p>

      <ul className="hoja-docs">
        {plan.documentos.map((d) => {
          const puesto = elegidos.has(d.nombre)
          const lleno = !puesto && elegidos.size >= plan.tope
          const bloqueado = d.estado === 'sin_texto'
          return (
            <li key={d.nombre} className={bloqueado ? 'bloqueado' : ''}>
              <label>
                <input type="checkbox" checked={puesto} disabled={bloqueado || lleno}
                       onChange={() => onAlternar(d)} />
                <span className="hoja-doc">
                  <span className="hoja-doc-nombre">{d.nombre}</span>
                  <span className="hoja-doc-meta">
                    {d.categoria}
                    {d.estado === 'listo' && ' · ya leído, se usa al instante'}
                    {d.estado === 'por_leer' && ' · hay que descargarlo'}
                    {d.paginas ? ` · ${entero(d.paginas)} ${d.paginas === 1 ? 'página' : 'páginas'}` : ''}
                    {d.metodo === 'ocr' && ' · leído con OCR'}
                  </span>
                  {/* El motivo, siempre que lo haya. Un control deshabilitado sin explicación se
                      lee como una avería. */}
                  {bloqueado && <span className="hoja-doc-no">No se puede leer: {d.motivo}</span>}
                  {lleno && !bloqueado && (
                    <span className="hoja-doc-no">Ya tienes {entero(plan.tope)}; quita uno para
                      poner este</span>
                  )}
                </span>
              </label>
            </li>
          )
        })}
      </ul>

      {conOcr && (
        <p className="hoja-ocr">
          <IconoAviso aria-hidden="true" />
          <span>
            Alguno de los elegidos se leyó con <b>OCR</b> (reconocimiento óptico sobre un documento
            escaneado). El texto sale bien casi siempre, pero conviene <b>confirmar las cifras</b>
            contra el documento antes de usarlas.
          </span>
        </p>
      )}

      <div className="hoja-acciones">
        <button type="button" className="boton-primario" disabled={!elegidos.size}
                onClick={onArrancar}>
          Leer {entero(elegidos.size)} {elegidos.size === 1 ? 'documento' : 'documentos'} · 1 min
        </button>
        <button type="button" className="accion" onClick={onVolver}>Volver a la ficha</button>
      </div>
      <p className="descargo">
        Cada corrida gasta tokens y el costo queda registrado en administración. El texto que se
        extraiga queda guardado, así que volver a entrar a este proceso es casi inmediato.
      </p>
    </div>
  )
}

/* ── la caja del veredicto ────────────────────────────────────────────────── */

function CajaVeredicto({ ajuste, compacta, onDescartar, descartado }: {
  ajuste: AjusteVeredicto
  compacta?: boolean
  onDescartar?: () => void
  descartado?: boolean
}) {
  const { antes, despues, cambia } = ajuste
  return (
    <aside className={`veredicto-caja${compacta ? ' compacta' : ''}`}>
      <span className="rotulo">Cómo cambia tu veredicto</span>
      <div className="veredicto-cifras">
        {cambia && antes.afinidad !== null && (
          <>
            <span className="veredicto-antes cifra">{antes.afinidad}</span>
            <IconoSeguir className="veredicto-flecha" />
          </>
        )}
        <span className="cifra veredicto-despues">{despues.afinidad}</span>
        <span className="veredicto-sobre">/100</span>
      </div>
      <Banda banda={despues.banda} />
      <div className="veredicto-barra" aria-hidden="true">
        <i className={`b-${despues.banda}`} style={{ width: `${despues.afinidad}%` }} />
      </div>
      <p className="veredicto-prosa">{ajuste.prosa}</p>
      {onDescartar && (
        <button type="button" className="boton-primario" disabled={descartado}
                onClick={onDescartar}>
          {descartado ? <><IconoSi /> Descartado</> : 'Descartar con este motivo'}
        </button>
      )}
      {!cambia && (
        <p className="veredicto-nota">
          El pliego no movió la afinidad: sigue en «{ETIQUETA_BANDA[despues.banda]}».
        </p>
      )}
    </aside>
  )
}

/* ── estado 2 · el informe ────────────────────────────────────────────────── */

/** Los títulos que el prompt garantiza (`_SECCIONES_REQ` en `app/analizar_proceso.py`). Se buscan
 *  por su texto porque son parte del contrato, con su prueba de blindaje en el servidor. */
const SECCIONES = {
  contrata: 'Qué se contrata',
  exigen: 'Qué te exigen',
  preparar: 'Qué debes preparar',
  fuera: 'Requisitos que podrían dejarte fuera',
  convocante: 'Cómo compra este convocante',
  decidir: 'Antes de decidir',
}

function Informe({ bloques, ajuste, analizados, descartado, onDescartar, onPreguntar,
                   analizadoEn, desactualizado, onRehacer }: {
  bloques: Bloque[]
  ajuste: AjusteVeredicto | null
  analizados: DocumentoLeido[]
  descartado: boolean
  onDescartar: () => void
  onPreguntar: () => void
  /** Epoch en segundos. `null` mientras no se sepa. */
  analizadoEn: number | null
  /** El corpus tiene documentos que no estaban cuando se analizó. */
  desactualizado: boolean
  onRehacer: () => void
}) {
  const secciones = useMemo(() => agrupar(bloques), [bloques])
  const de = (titulo: string) => secciones.get(titulo) ?? []
  // El titular es la primera frase de «Qué se contrata» solo si el pliego no trae un hallazgo
  // decisivo; si lo trae, manda ese, porque es lo que cambia la decisión.
  const intro = partirRespuesta(de(SECCIONES.contrata)).corta

  const hallazgos = de(SECCIONES.fuera).flatMap((b) =>
    b.tipo === 'lista' ? b.items.map(partirHallazgo) : [])
  // Las secciones que el rediseño no dibuja arriba pero que el prompt SIGUE generando. Se muestran
  // plegadas, no se tiran: `docs/oportunidades.md` es explícito en que esta detección no se recorta.
  const extra = [...de(SECCIONES.convocante), ...de(SECCIONES.decidir)]

  return (
    <article className="informe">
      <header className={`informe-cabeza${ajuste ? '' : ' solo-informe'}`}>
        <div>
          <span className="rotulo">
            Informe del pliego{analizadoEn ? ` · leído ${haceCuanto(analizadoEn)}` : ''}
          </span>
          <h1>
            {ajuste?.decisivo
              ? `Te exigen ${ajuste.decisivo.toLowerCase()}: es lo que decide si puedes presentarte`
              : 'Esto es lo que te exige el pliego'}
          </h1>
          {intro && <p className="informe-intro"><Trozos inline={intro} /></p>}
          <p className="informe-fuente">
            {analizados.length > 0 && (
              // Las categorías se DEDUPLICAN: «Pliego de condiciones, Pliego de condiciones,
              // Estudios previos, Estudios previos, Pliego de condiciones» no informa de nada y
              // hace dudar de si de verdad leyó cinco documentos distintos.
              <>Leí {entero(analizados.length)}{' '}
                {analizados.length === 1 ? 'documento' : 'documentos'}:{' '}
                {[...new Set(analizados.map((d) => d.categoria))].join(', ').toLowerCase()}.</>
            )}
          </p>
        </div>
        {ajuste && (
          <CajaVeredicto ajuste={ajuste} onDescartar={onDescartar} descartado={descartado} />
        )}
      </header>

      {desactualizado && (
        // Va ARRIBA y no en el pie: cambia cómo hay que leer todo lo que sigue. Un informe hecho
        // sobre el pliego anterior a una adenda puede estar describiendo requisitos que ya cambiaron.
        <div className="informe-viejo" role="status">
          <IconoAviso />
          <p>
            Desde que se leyó este pliego han aparecido documentos nuevos del proceso —normalmente
            una adenda—. Lo que sigue describe la versión anterior.{' '}
            <button type="button" className="enlace" onClick={onRehacer}>
              Volver a analizar con los documentos de ahora
            </button>
          </p>
        </div>
      )}

      {hallazgos.length > 0 && (
        <section className="informe-seccion">
          <h2>{SECCIONES.fuera}</h2>
          <div className="hallazgos">
            {hallazgos.map((h, i) => {
              const cruce = cruzarHallazgo(h, ajuste?.requisitos ?? [])
              return (
                // El fondo de severidad se APAGA cuando el requisito no te deja fuera. La
                // severidad sigue ahí, en su etiqueta y en su palabra: describe el PLIEGO —si
                // restringe la competencia—, que es una pregunta distinta de si tú puedes
                // presentarte. Antes el color respondía la primera y se leía como la segunda.
                <div key={i} className={`hallazgo sev-${h.severidad ?? 'neutra'}`
                                        + (cruce?.cumples === true ? ' cumplido' : '')
                                        + (cruce?.cumples === 'parcial' ? ' parcial' : '')
                                        + (cruce?.cumples === false ? ' te-deja-fuera' : '')}>
                  <div className="hallazgo-marcas">
                    {h.severidad && <span className="sev">{ETIQUETA_SEV[h.severidad]}</span>}
                    {cruce && <MarcaCruce cruce={cruce} />}
                  </div>
                  <p className="hallazgo-texto"><Trozos inline={h.texto} /></p>
                  {/* La cita va en serif e itálica entre comillas angulares: es la PRUEBA. Si el
                      modelo no la trajo verbatim, el servidor ya la habría descartado del JSON —
                      aquí se muestra tal cual llegó, para que se pueda buscar en el pliego. */}
                  {h.cita && <blockquote className="cita">«{h.cita}»</blockquote>}
                  {cruce && (
                    <p className="hallazgo-cruce">
                      {cruce.requisitos.map((r) => (
                        <span key={r.etiqueta} className={`cruce-dato cruce-${r.signo}`}>
                          {r.etiqueta}: {r.exigido} · {r.tuyo}
                        </span>
                      ))}
                      {cruce.cumples === true && (
                        <span className="cruce-nota">
                          Sigue siendo una señal sobre el pliego; a ti no te deja fuera.
                        </span>
                      )}
                    </p>
                  )}
                </div>
              )
            })}
          </div>
        </section>
      )}

      {ajuste && ajuste.requisitos.length > 0 && (
        <section className="informe-seccion">
          <h2>Qué te exigen, cruzado con tu perfil</h2>
          <ul className="requisitos">
            {ajuste.requisitos.map((r) => <Requisito key={r.clave} r={r} />)}
          </ul>
        </section>
      )}

      <div className="informe-columnas">
        {de(SECCIONES.exigen).length > 0 && (
          <section className="informe-seccion">
            <h2>{SECCIONES.exigen}</h2>
            {de(SECCIONES.exigen).map((b, i) => <Pieza key={i} bloque={b} />)}
          </section>
        )}
        {de(SECCIONES.preparar).length > 0 && (
          <section className="informe-seccion">
            <h2>{SECCIONES.preparar}</h2>
            {de(SECCIONES.preparar).map((b, i) => <Pieza key={i} bloque={b} numerada />)}
          </section>
        )}
      </div>

      {extra.length > 0 && (
        <details className="informe-extra">
          <summary>Cómo compra este convocante y qué verificar antes de decidir</summary>
          {extra.map((b, i) => <Pieza key={i} bloque={b} />)}
        </details>
      )}

      <footer className="informe-pie">
        <button type="button" className="accion" onClick={onPreguntar}>
          Preguntar al asistente sobre el pliego
        </button>
        {/* Rehacerlo es lo ÚNICO de esta pantalla que vuelve a costar, así que se dice. Discreto y
            no un botón primario: lo normal es leer el que ya está, no pagar otro igual. */}
        <button type="button" className="enlace informe-rehacer" onClick={onRehacer}>
          Volver a analizar
        </button>
        <p className="descargo">
          Lo leyó un modelo de lenguaje sobre los documentos publicados en SECOP: verifica las citas
          en el pliego antes de decidir. Las citas van textuales entre comillas angulares.
          {analizadoEn ? ' Volver a analizarlo lee otra vez los documentos y tarda cerca de un minuto.' : ''}
        </p>
      </footer>
    </article>
  )
}

const ETIQUETA_SEV: Record<Severidad, string> = { alta: 'Alta', media: 'Media', baja: 'Baja' }

/** La insignia de si ESTE hallazgo te deja fuera. La palabra, no solo el color: se lee igual en
 *  blanco y negro y para quien no distingue el verde del rojo. */
function MarcaCruce({ cruce }: { cruce: Cruce }) {
  if (cruce.cumples === true) {
    return <span className="marca-cruce ok"><IconoSi /> Tú lo cumples</span>
  }
  if (cruce.cumples === false) {
    return <span className="marca-cruce no"><IconoNo /> Esto te deja fuera</span>
  }
  return <span className="marca-cruce parcial"><IconoSinDato /> Falta verificar parte</span>
}

function Requisito({ r }: { r: RequisitoPliego }) {
  const Icono = r.signo === 'ok' ? IconoSi : r.signo === 'no' ? IconoNo : IconoSinDato
  return (
    <li className={`req req-${r.signo}`}>
      <span className="req-marca" aria-hidden="true"><Icono /></span>
      <span className="req-cuerpo">
        <b>{r.etiqueta}</b>: {r.exigido} · <span className="req-tuyo">{r.tuyo}</span>
        {r.arreglo && <span className="req-arreglo">{r.arreglo}</span>}
        {r.cita && <blockquote className="cita">«{r.cita}»</blockquote>}
      </span>
    </li>
  )
}

/* ── piezas ───────────────────────────────────────────────────────────────── */

/** Agrupa los bloques por el título de sección que los precede. */
function agrupar(bloques: Bloque[]): Map<string, Bloque[]> {
  const fuera = new Map<string, Bloque[]>()
  let actual: string | null = null
  for (const b of bloques) {
    if (b.tipo === 'titulo') {
      actual = b.texto
      if (!fuera.has(actual)) fuera.set(actual, [])
    } else if (actual) {
      fuera.get(actual)!.push(b)
    }
  }
  return fuera
}

function Pieza({ bloque, numerada }: { bloque: Bloque; numerada?: boolean }) {
  switch (bloque.tipo) {
    case 'parrafo':
      return <p><Trozos inline={bloque.inline} /></p>
    case 'lista':
      return numerada ? (
        <ol className="lista-numerada">
          {bloque.items.map((it, i) => <li key={i}><Trozos inline={it} /></li>)}
        </ol>
      ) : (
        <ul className="lista">
          {bloque.items.map((it, i) => <li key={i}><Trozos inline={it} /></li>)}
        </ul>
      )
    case 'tabla':
      return (
        <div className="tabla-envoltura">
          <table className="tabla-respuesta">
            <thead>
              <tr>{bloque.cabeceras.map((c, i) => <th key={i}><Trozos inline={c} /></th>)}</tr>
            </thead>
            <tbody>
              {bloque.filas.map((f, i) => (
                <tr key={i}>{f.map((c, j) => <td key={j}><Trozos inline={c} /></td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>
      )
    case 'codigo':
      return <pre className="codigo">{bloque.texto}</pre>
    default:
      return null
  }
}

/** Los trozos en línea del informe. A diferencia del asistente aquí NO hay hilo al que mandar una
 *  pregunta, así que un `secop:` se pinta como texto: un enlace que no lleva a ninguna parte es
 *  peor que el nombre a secas. */
function Trozos({ inline }: { inline: Inline[] }) {
  return (
    <>
      {inline.map((t, i) => {
        switch (t.tipo) {
          case 'fuerte':
            return <b key={i}>{t.texto}</b>
          case 'codigo':
            return <code key={i}>{t.texto}</code>
          case 'enlace':
            return <a key={i} href={t.href} target="_blank" rel="noopener noreferrer">{t.texto}</a>
          default:
            return <span key={i}>{t.texto}</span>
        }
      })}
    </>
  )
}

/**
 * El nombre del documento como se puede leer: sin el «16. » del orden de publicación en SECOP ni la
 * extensión. Se prefiere al `categoria` porque tres «Pliego de condiciones» seguidos no distinguen
 * nada, y quien espera quiere saber CUÁL está leyendo.
 */
export function limpiarNombre(nombre: string | undefined): string {
  return (nombre ?? '')
    .replace(/^\s*\d+[.)-]\s*/, '')
    .replace(/\.(pdf|docx?|xlsx?|zip)$/i, '')
    .trim()
}

export const _pruebas = { agrupar, limpiarNombre }
export type { ClaseSecop }
